// Libraries
import { useMemo } from 'react';
import { useBreakpointValue } from '@chakra-ui/react';
// Types
import { UseBorderRightProps } from './useBorderRight.types';

/**
 * Determines whether a cell should have a right border based on its ID and a list of header names.
 *
 * @param {string} id - The ID of the cell.
 * @param {string[]} headers - The list of header names.
 * @returns {string} The border style for the cell ('section' if it should have a right border, 'none' otherwise).
 */
const shouldHaveBorderRight = (id: string, headers: string[]) => {
  return !!headers?.find((headerName) => id.includes(headerName)) ? 'section' : 'none';
};

/**
 * Hook to determine the border right style for a cell based on the header and width mapping.
 * @param {UseBorderRightProps} props
 * @param {string} props.cellId The ID of the cell.
 * @param {BorderRightBreakpoint} props.cellsWithBorderRightMap The mapping of cells with border right styles.
 * @returns The border right style for the cell.
 */
export const useBorderRight = <CellsName,>({ cellId, cellsWithBorderRightMap }: UseBorderRightProps<CellsName>) => {
  const headersWidthBorderRight = useBreakpointValue(cellsWithBorderRightMap, {
    ssr: false,
  }) as string[];
  /**
   * Determines the border right style for the cell based on the header and width mapping.
   */
  const borderRight = useMemo(
    () => shouldHaveBorderRight(cellId, headersWidthBorderRight),
    [cellId, headersWidthBorderRight]
  );
  return borderRight;
};
